import apiClient from '@/services/http/client'
import type { ApiListResponse } from '@/types'
import type {
  WhatsappAccount,
  ConnectAccountPayload,
  OAuthConnectPayload,
  StudioAgentConfig,
  StoreAgentConfigPayload,
  UpdateAgentConfigPayload,
  BotIntent,
  StoreBotIntentPayload,
  UpdateBotIntentPayload,
  WhatsappConversation,
  WhatsappMessage,
  WhatsappSetupRequest,
  CreateSetupRequestPayload,
  ConvertToStudentPayload,
  ConvertToStudentResult,
  WaitlistEntry,
} from './types'

// ─── WhatsApp Account ─────────────────────────────────────────────────────────
// GET  /api/v1/whatsapp/account            → 200 { data: WhatsappAccount } | 404
// POST /api/v1/whatsapp/account            → 200/201 { data: WhatsappAccount }
// POST /api/v1/whatsapp/account/activate   → 200 { data: WhatsappAccount }
// POST /api/v1/whatsapp/account/deactivate → 200 { data: WhatsappAccount }
// Requiere header X-Studio-Id + role: owner | admin

export const fetchWhatsappAccount = async (): Promise<WhatsappAccount | null> => {
  try {
    const res = await apiClient.get<{ data: WhatsappAccount }>('/api/v1/whatsapp/account')
    return res.data.data
  } catch (err: unknown) {
    if ((err as { response?: { status?: number } }).response?.status === 404) return null
    throw err
  }
}

export const connectWhatsappAccount = async (payload: ConnectAccountPayload): Promise<WhatsappAccount> => {
  const res = await apiClient.post<{ data: WhatsappAccount }>('/api/v1/whatsapp/account', payload)
  return res.data.data
}

export const activateWhatsappAccount = async (): Promise<WhatsappAccount> => {
  const res = await apiClient.post<{ data: WhatsappAccount }>('/api/v1/whatsapp/account/activate')
  return res.data.data
}

export const deactivateWhatsappAccount = async (): Promise<WhatsappAccount> => {
  const res = await apiClient.post<{ data: WhatsappAccount }>('/api/v1/whatsapp/account/deactivate')
  return res.data.data
}

// POST /api/v1/whatsapp/account/oauth
// Exchanges the Meta Embedded Signup authorization code for credentials,
// subscribes the WABA webhook, and activates the account in one request.
export const oauthConnectWhatsappAccount = async (payload: OAuthConnectPayload): Promise<WhatsappAccount> => {
  const res = await apiClient.post<{ data: WhatsappAccount }>('/api/v1/whatsapp/account/oauth', payload)
  return res.data.data
}

// ─── Agent Config ─────────────────────────────────────────────────────────────
// GET    /api/v1/whatsapp/agent-config        → 200 { data: StudioAgentConfig } | 404
// POST   /api/v1/whatsapp/agent-config        → 201 { data: StudioAgentConfig }
// PATCH  /api/v1/whatsapp/agent-config        → 200 { data: StudioAgentConfig }
// Requiere header X-Studio-Id + role: owner | admin

export const fetchAgentConfig = async () => {
  const res = await apiClient.get<{ data: StudioAgentConfig }>('/api/v1/whatsapp/agent-config')
  return res.data.data
}

export const createAgentConfig = async (payload: StoreAgentConfigPayload) => {
  const res = await apiClient.post<{ data: StudioAgentConfig }>('/api/v1/whatsapp/agent-config', payload)
  return res.data.data
}

export const updateAgentConfig = async (payload: UpdateAgentConfigPayload) => {
  const res = await apiClient.patch<{ data: StudioAgentConfig }>('/api/v1/whatsapp/agent-config', payload)
  return res.data.data
}

// ─── Bot Intents ──────────────────────────────────────────────────────────────
// GET    /api/v1/whatsapp/bot-intents         → 200 { data: BotIntent[] }
// POST   /api/v1/whatsapp/bot-intents         → 201 { data: BotIntent }
// GET    /api/v1/whatsapp/bot-intents/:id     → 200 { data: BotIntent }
// PATCH  /api/v1/whatsapp/bot-intents/:id     → 200 { data: BotIntent }
// DELETE /api/v1/whatsapp/bot-intents/:id     → 200 { message: string }
// Requiere header X-Studio-Id + role: owner | admin

export const fetchBotIntents = async () => {
  const res = await apiClient.get<ApiListResponse<BotIntent>>('/api/v1/whatsapp/bot-intents')
  return res.data.data
}

export const fetchBotIntent = async (id: number) => {
  const res = await apiClient.get<{ data: BotIntent }>(`/api/v1/whatsapp/bot-intents/${id}`)
  return res.data.data
}

export const createBotIntent = async (payload: StoreBotIntentPayload) => {
  const res = await apiClient.post<{ data: BotIntent }>('/api/v1/whatsapp/bot-intents', payload)
  return res.data.data
}

export const updateBotIntent = async (id: number, payload: UpdateBotIntentPayload) => {
  const res = await apiClient.patch<{ data: BotIntent }>(`/api/v1/whatsapp/bot-intents/${id}`, payload)
  return res.data.data
}

export const deleteBotIntent = async (id: number) => {
  await apiClient.delete(`/api/v1/whatsapp/bot-intents/${id}`)
}

// ─── Conversations ────────────────────────────────────────────────────────────
// GET  /api/v1/whatsapp/conversations               → 200 { data: WhatsappConversation[] }
// GET  /api/v1/whatsapp/conversations/:id           → 200 { data: WhatsappConversation }
// GET  /api/v1/whatsapp/conversations/:id/messages  → 200 { data: WhatsappMessage[] }
// POST /api/v1/whatsapp/conversations/:id/messages  → 200  (requiere role staff+)
// POST /api/v1/whatsapp/conversations/:id/escalate  → 200  (requiere role staff+)
// POST /api/v1/whatsapp/conversations/:id/close     → 200  (requiere role staff+)
// POST /api/v1/whatsapp/conversations/:id/reset-bot → 200  (requiere role staff+)
// POST /api/v1/whatsapp/conversations/:id/reopen    → 200  (requiere role staff+)
// Requiere header X-Studio-Id

export const fetchConversations = async () => {
  const res = await apiClient.get<ApiListResponse<WhatsappConversation>>('/api/v1/whatsapp/conversations')
  return res.data.data
}

export const fetchConversation = async (id: number) => {
  const res = await apiClient.get<{ data: WhatsappConversation }>(`/api/v1/whatsapp/conversations/${id}`)
  return res.data.data
}

export const fetchConversationMessages = async (id: number) => {
  const res = await apiClient.get<ApiListResponse<WhatsappMessage>>(`/api/v1/whatsapp/conversations/${id}/messages`)
  return res.data.data
}

export const sendMessage = async (conversationId: number, body: string) => {
  await apiClient.post(`/api/v1/whatsapp/conversations/${conversationId}/messages`, { body })
}

export const escalateConversation = async (conversationId: number) => {
  await apiClient.post(`/api/v1/whatsapp/conversations/${conversationId}/escalate`)
}

export const closeConversation = async (conversationId: number) => {
  await apiClient.post(`/api/v1/whatsapp/conversations/${conversationId}/close`)
}

export const resetConversationBot = async (conversationId: number): Promise<WhatsappConversation> => {
  const res = await apiClient.post<{ data: WhatsappConversation }>(
    `/api/v1/whatsapp/conversations/${conversationId}/reset-bot`,
  )
  return res.data.data
}

export const reopenConversation = async (conversationId: number): Promise<WhatsappConversation> => {
  const res = await apiClient.post<{ data: WhatsappConversation }>(
    `/api/v1/whatsapp/conversations/${conversationId}/reopen`,
  )
  return res.data.data
}

export const convertToStudent = async (
  conversationId: number,
  payload: ConvertToStudentPayload,
): Promise<ConvertToStudentResult> => {
  const res = await apiClient.post<{ data: ConvertToStudentResult }>(
    `/api/v1/whatsapp/conversations/${conversationId}/convert-to-student`,
    payload,
  )
  return res.data.data
}

// ─── Setup Requests ───────────────────────────────────────────────────────────
// GET  /api/v1/whatsapp/setup-requests/latest → 200 { data: WhatsappSetupRequest } | 404
// POST /api/v1/whatsapp/setup-requests        → 201 { data: WhatsappSetupRequest }
// Requiere header X-Studio-Id + role: owner | admin

export const fetchLatestSetupRequest = async (): Promise<WhatsappSetupRequest | null> => {
  try {
    const res = await apiClient.get<{ data: WhatsappSetupRequest }>(
      '/api/v1/whatsapp/setup-requests/latest',
    )
    return res.data.data
  } catch (err: unknown) {
    if ((err as { response?: { status?: number } }).response?.status === 404) return null
    throw err
  }
}

export const createSetupRequest = async (
  payload: CreateSetupRequestPayload,
): Promise<WhatsappSetupRequest> => {
  const res = await apiClient.post<{ data: WhatsappSetupRequest }>(
    '/api/v1/whatsapp/setup-requests',
    payload,
  )
  return res.data.data
}

// ─── Waitlist ─────────────────────────────────────────────────────────────────
// GET /api/v1/whatsapp/waitlist?class_session_id=X → 200 { data: WaitlistEntry[] }

export const fetchWaitlist = async (classSessionId: number): Promise<WaitlistEntry[]> => {
  const res = await apiClient.get<{ data: WaitlistEntry[] }>(
    '/api/v1/whatsapp/waitlist',
    { params: { class_session_id: classSessionId } },
  )
  return res.data.data
}
